// src/components/SessionBar/SessionBar.jsx
import { ROUTES } from '../../const/routes';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';
import { useUser } from '../../context/UserContext';
import { useTranslation } from 'react-i18next';
import {
  AVATAR_POR_DEFECTO,
  getAvatarSrc,
  handleAvatarError,
} from '../../utils/avatar';

const SessionBar = React.memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData, clearUserData } = useUser();
  const [showOptions, setShowOptions] = useState(false);

  const handleToggleOptions = () => {
    setShowOptions((prev) => !prev);
  };

  const closeMenu = () => {
    setShowOptions(false);
  };

  const verPerfil = () => {
    setShowOptions(false);
    navigate(ROUTES.perfil);
  };

  const verMovimientos = () => {
    setShowOptions(false);
    navigate(ROUTES.movimientos);
  };

  const handleLogout = async () => {
    setShowOptions(false);
    try {
      await axiosInstance.post('/api/auth/logout', {});
      clearUserData();
      navigate(ROUTES.inicio);
    } catch (error) {
      clearUserData();
      navigate(ROUTES.inicio);
    }
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const saldoActual = userData?.saldo || 0;
  const nickActual = userData?.nick_usuario || '-';
  const avatarActual = userData?.avatar || AVATAR_POR_DEFECTO;

  // Menú desplegable reutilizable
  const dropdownMenu = showOptions && (
    <>
      {/* Overlay invisible para cerrar al hacer click fuera */}
      <div className="fixed inset-0 z-40" onClick={closeMenu} />
      <div className="absolute right-0 mt-2 z-50 overflow-hidden rounded-xl border border-slate-200 bg-slate-900 shadow-[0_4px_24px_rgba(139,92,246,0.35)]" style={{ minWidth: '140px', maxWidth: 'calc(100vw - 16px)' }}>
        <ul className="py-1">
          <li
            className="px-4 py-2.5 cursor-pointer text-xs sm:text-sm text-slate-200 hover:bg-violet-600/20 hover:text-white transition-colors whitespace-nowrap"
            onClick={verPerfil}
          >
            {t('sessionBar.viewProfile')}
          </li>
          <li
            className="px-4 py-2.5 cursor-pointer text-xs sm:text-sm text-slate-200 hover:bg-violet-600/20 hover:text-white transition-colors whitespace-nowrap"
            onClick={verMovimientos}
          >
            {t('sessionBar.movements')}
          </li>
          <li
            className="px-4 py-2.5 cursor-pointer text-xs sm:text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold transition-colors whitespace-nowrap border-t border-slate-700/60"
            onClick={handleLogout}
          >
            {t('sessionBar.logout')}
          </li>
        </ul>
      </div>
    </>
  );

  return (
    <div className="py-2 md:py-3 xl:py-4 border-b-4 border-slate-200 bg-slate-950 text-white w-full sticky top-0 z-50 shadow-violet-500/50 shadow-[0_5px_15px_0_rgba(148,0,211,0.5)]">
      {/* Vista desktop */}
      <div className="hidden md:block px-4 xl:px-10">
        <div className="flex justify-end items-center max-w-[1400px] mx-auto pr-2">
          <div className="flex items-center space-x-2 md:space-x-3 xl:space-x-4">
            <input
              type="text"
              value={`$ ${saldoActual ? formatCurrency(saldoActual) : '-'}`}
              readOnly
              className="py-2 px-3 md:px-3 xl:px-4 bg-gray-800 text-white text-sm md:text-base xl:text-lg focus:outline-none rounded border-4 border-slate-200"
              style={{ width: '14ch' }}
            />

            <div className="relative">
              <div className="flex items-center space-x-2 md:space-x-3 xl:space-x-4 hover:brightness-125">
                <span className="text-yellow-500 text-sm whitespace-nowrap">
                  {nickActual ? nickActual : t('sessionBar.loading')}
                </span>
                <img
                  src={getAvatarSrc(avatarActual)}
                  alt="Avatar"
                  onError={handleAvatarError}
                  className="w-10 h-10 md:w-12 md:h-12 xl:w-14 xl:h-14 rounded-full border-4 cursor-pointer border-slate-200 bg-gray-600 hover:filter hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]"
                  onClick={handleToggleOptions}
                />
              </div>
              {dropdownMenu}
            </div>
          </div>
        </div>
      </div>

      {/* Vista móvil centrada */}
      <div className="md:hidden flex justify-center items-center px-1">
        <div className="flex items-center space-x-1.5">
          <input
            type="text"
            value={`$ ${saldoActual ? formatCurrency(saldoActual) : '-'}`}
            readOnly
            className="py-1 px-1.5 bg-gray-800 text-white text-[11px] focus:outline-none rounded border-4 border-slate-200"
            style={{ width: '14ch' }}
          />

          <div className="relative">
            <div className="flex items-center space-x-1.5 hover:brightness-125">
              <span className="text-yellow-500 text-xs whitespace-nowrap">
                {nickActual ? nickActual : t('sessionBar.loading')}
              </span>
              <img
                src={getAvatarSrc(avatarActual)}
                alt="Avatar"
                onError={handleAvatarError}
                className="w-9 h-9 rounded-full border-4 cursor-pointer border-slate-200 bg-gray-600 hover:filter hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]"
                onClick={handleToggleOptions}
              />
            </div>
            {dropdownMenu}
          </div>
        </div>
      </div>
    </div>
  );
});

export default SessionBar;
